import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db4bs6puu2cnvnf27big';
const CITY_OPTIONS = ['Long Beach', 'Lakewood', 'Downey', 'Cypress', 'Seal Beach', 'Other'];
const TYPE_OPTIONS = ['Charity', "Women's", 'Club', 'Other'];

type Status = 'idle' | 'sending' | 'success' | 'error';
type FormResponse = { code?: string; message?: string; meta?: { message?: string; detail?: string } };

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-4 py-3 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-xs text-foreground-600 mb-1.5';

export default function ListEventForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (String(data.get('website_alt') || '').trim()) {
      setStatus('success');
      setMessage('Thanks! Your event was submitted and is queued for review.');
      form.reset();
      return;
    }

    const email = String(data.get('email') || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address so we can follow up.');
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      const v = String(value).trim();
      if (v) payload.append(key, v);
    });

    setStatus('sending');
    setMessage('');
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('Thanks! Your event was submitted and is queued for review.');
        form.reset();
      } else {
        setStatus('error');
        setMessage(typeof serverMsg === 'string' && serverMsg ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section id="list-event" className="bg-foreground-950 px-4 md:px-8 py-14 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="font-label uppercase tracking-[0.25em] text-sm text-accent-400 font-bold">Submit</p>
          <h2 className="font-heading font-extrabold uppercase text-4xl md:text-6xl text-background-50 leading-none mt-2">List your event free</h2>
          <p className="mt-4 text-background-200 max-w-md">
            Charity scrambles, women&apos;s events and club tournaments are all welcome. Send us the details and we&apos;ll add it to the calendar.
          </p>
          <ul className="mt-6 space-y-3 text-background-200 text-sm">
            {['No cost to list a community or charity event.', 'Confirmed dates appear on the calendar right away.', 'We follow up by email if we need anything else.'].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center text-accent-400"><i className="ri-check-line"></i></span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form data-readdy-form id="list-event-form" onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-5 md:p-7 border-t-4 border-accent-500">
            <input type="text" name="website_alt" className="gs-field-x" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="event_name" className={labelClass}>Event name</label>
                <input id="event_name" name="event_name" type="text" required placeholder="e.g. Fore The Kids Golf Classic" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="event_date" className={labelClass}>Event date</label>
                <input id="event_date" name="event_date" type="date" required className={fieldClass} />
              </div>
              <div>
                <label htmlFor="course" className={labelClass}>Course</label>
                <input id="course" name="course" type="text" required placeholder="e.g. Virginia Country Club" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="city" className={labelClass}>City</label>
                <div className="relative">
                  <select id="city" name="city" required defaultValue="" className={`${fieldClass} appearance-none pr-10 cursor-pointer`}>
                    <option value="" disabled>Choose a city</option>
                    {CITY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-foreground-600"><i className="ri-arrow-down-s-line"></i></span>
                </div>
              </div>
              <div>
                <label htmlFor="event_type" className={labelClass}>Event type</label>
                <div className="relative">
                  <select id="event_type" name="event_type" required defaultValue="" className={`${fieldClass} appearance-none pr-10 cursor-pointer`}>
                    <option value="" disabled>Choose a type</option>
                    {TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none text-foreground-600"><i className="ri-arrow-down-s-line"></i></span>
                </div>
              </div>
              <div>
                <label htmlFor="name" className={labelClass}>Your name</label>
                <input id="name" name="name" type="text" required placeholder="First and last name" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <input id="email" name="email" type="email" required placeholder="you@example.com" className={fieldClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="phone" className={labelClass}>Phone <span className="normal-case tracking-normal text-foreground-400">(optional)</span></label>
                <input id="phone" name="phone" type="tel" placeholder="562-000-0000" className={fieldClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="notes" className={labelClass}>Notes <span className="normal-case tracking-normal text-foreground-400">(optional)</span></label>
                <textarea id="notes" name="notes" rows={4} maxLength={500} placeholder="Who it benefits, format, times, entry info — up to 500 characters." className={`${fieldClass} resize-none`}></textarea>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <button type="submit" disabled={status === 'sending'} className="bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider px-6 py-3 rounded-md whitespace-nowrap cursor-pointer disabled:opacity-60">
                {status === 'sending' ? 'Sending...' : 'Submit your event'}
              </button>
              <p className="text-xs text-foreground-500">Free to list. We may edit for clarity before publishing.</p>
            </div>

            {status === 'success' && <p className="mt-4 text-sm text-primary-600 font-medium">{message}</p>}
            {status === 'error' && <p className="mt-4 text-sm text-secondary-700 font-medium">{message}</p>}
          </form>
        </div>
      </div>
    </section>
  );
}